import { useRef, useState } from 'react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { InvitationPreview } from '../InvitationPreview';
import { CORNERS, DEFAULT_PERSPECTIVE, normalizePerspective } from '../../utils/perspective';

const NAMES = { tl: 'Top left', tr: 'Top right', br: 'Bottom right', bl: 'Bottom left' };
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const round = (v) => Math.round(v * 10) / 10;

/**
 * Side-view (mockup) templates: the admin drags the four corners onto the card in the background photo.
 * The flat design is then warped onto those corners everywhere (editor, previews, SMS/MMS, print).
 * value = { tl: [x, y], tr, br, bl } in % of the page, or null for a normal flat template.
 */
export const PerspectivePicker = ({ image, value, onChange, fields = [] }) => {
  const boxRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const on = Boolean(value);
  const valid = on && Boolean(normalizePerspective(value));

  const pointPct = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    return [round(clamp(((e.clientX - r.left) / r.width) * 100, -20, 120)), round(clamp(((e.clientY - r.top) / r.height) * 100, -20, 120))];
  };

  const onMove = (e) => {
    if (!drag || drag.pointerId !== e.pointerId) return;
    onChange({ ...value, [drag.corner]: pointPct(e) });
  };

  return (
    <div className="space-y-3 rounded-lg border border-[#E6E1D6] p-3" data-testid="perspective-picker">
      <div className="flex items-center gap-2">
        <Checkbox
          id="perspective-on"
          checked={on}
          onCheckedChange={(checked) => onChange(checked ? { ...DEFAULT_PERSPECTIVE } : null)}
        />
        <Label htmlFor="perspective-on" className="cursor-pointer">
          Side view (mockup): the background is a photo of the card at an angle
        </Label>
      </div>
      {on ? (
        <>
          <p className="text-xs text-[#6B665C]">
            Drag the four dots onto the corners of the card in the photo. Design the text on the flat page as usual;
            it is bent onto the card in the editor, the previews, SMS pictures and downloads.
          </p>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <div
                ref={boxRef}
                className="relative w-full overflow-visible bg-cream select-none touch-none"
                style={{ aspectRatio: '8.5 / 11' }}
                onPointerMove={onMove}
                onPointerUp={() => setDrag(null)}
                onPointerCancel={() => setDrag(null)}
              >
                {image ? <img src={image} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover pointer-events-none" /> : null}
                <svg className="absolute inset-0 h-full w-full overflow-visible pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                  <polygon
                    points={CORNERS.map((k) => value[k].join(',')).join(' ')}
                    fill={valid ? 'rgba(236,0,140,0.12)' : 'rgba(176,0,58,0.25)'}
                    stroke={valid ? '#EC008C' : '#B0003A'}
                    strokeWidth="0.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                {CORNERS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    aria-label={`${NAMES[k]} corner`}
                    title={`${NAMES[k]} (${value[k][0]}%, ${value[k][1]}%)`}
                    className="absolute h-4 w-4 -ml-2 -mt-2 rounded-full bg-white border-2 border-[#EC008C] shadow cursor-grab touch-none"
                    style={{ left: `${value[k][0]}%`, top: `${value[k][1]}%` }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      try {
                        boxRef.current.setPointerCapture(e.pointerId);
                      } catch (err) {
                        // best effort
                      }
                      setDrag({ corner: k, pointerId: e.pointerId });
                    }}
                    data-testid={`perspective-${k}`}
                  />
                ))}
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className={valid ? 'text-xs text-[#6B665C]' : 'text-xs font-semibold text-[#B0003A]'}>
                  {valid ? 'Corners go clockwise from the top left.' : 'The corners cross or are too close: this will print flat.'}
                </span>
                <Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...DEFAULT_PERSPECTIVE })}>
                  Reset
                </Button>
              </div>
            </div>
            <div>
              <InvitationPreview
                backgroundImage={image}
                fields={fields}
                perspective={value}
                getText={(f) => f.default_text || f.text || ''}
                alt="Side view preview"
                eager
              />
              <p className="text-xs text-[#6B665C] mt-2">Preview with the sample wording</p>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PerspectivePicker;
